import { One, OrderDirection } from '@granlupo/api-client/generated/content/zeus'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useIsAllowedToFullUse } from '../../hooks/useIsAllowedToFullUse'
import { RespondWithNotFound, serverPropsHandler } from '../../libs/next/api/handlers'
import { createFetcher } from '../../libs/next/api/jsonApiHandler'
import { websiteContent } from '../../libs/next/loaders/graphql/src/contentClient'
import { ProductIntroSummary } from '../../src/components/product/ProductIntroSummary'
import { Button } from '../../src/components/shared/Button'
import { Collapsible } from '../../src/components/shared/Collapsible'
import { Container } from '../../src/components/shared/Container'
import { EvaluationTemplate } from '../../src/components/shared/EvaluationTemplate'
import { Layout } from '../../src/components/shared/Layout'
import { ListProductReview } from '../../src/components/shared/ListProductReview'
import { LoginModal } from '../../src/components/shared/LoginModal'
import { ProductCarousel } from '../../src/components/shared/ProductCarousel'
import { Rating } from '../../src/components/shared/Rating'
import { SuggestionModal } from '../../src/components/shared/SuggestionModal'
import { SummaryBanner } from '../../src/components/shared/SummaryBanner'
import { Tabs } from '../../src/components/shared/Tabs'
import { IngredientsTab } from '../../src/components/tabs/IngredientsTab'
import { ManufacturerTab } from '../../src/components/tabs/ManufacturerTab'
import { SummaryTab } from '../../src/components/tabs/SummaryTab'
import { SummaryTabMobile } from '../../src/components/tabs/SummaryTabMobile'
import { recommendedActivity } from '../../src/const/ActivityHelper'
import { InternalLink } from '../../src/const/Links'
import { setSeoProduct } from '../../src/const/setSeo'
import { Translation, useTranslate } from '../../src/contexts/TranslationContext'
import { BrandTabFragment } from '../../src/data/BrandFragment'
import { ConsumptionMessageFragment } from '../../src/data/ConsumptionMessageFragment'
import { EvaluationInfoFragment } from '../../src/data/EvaluationInfoFragment'
import { ItemSubCategoryPreviewFragment, ItemSubCategorySummaryFragment } from '../../src/data/ItemSubCategoryFragment'
import { LayoutFragment } from '../../src/data/LayoutFragment'
import { ProductEvaluationSummaryFragment } from '../../src/data/ProductEvaluationSummaryFragment'
import { ProductFeaturesFragment } from '../../src/data/ProductFeaturesFragment'
import { ProductDetailFragment } from '../../src/data/ProductFragment'
import { ProductReviewFragment } from '../../src/data/ProductReviewFragment'
import { ScoreFragment } from '../../src/data/ScoreFragment'
import style from '../../src/styles/ProductPageDetail.module.sass'
import SvgChevronDown from '../../src/svg/icons/ChevronDown'
import { useActiveDog } from '../../src/user/ActiveDogContext'
import { escapeRegExp } from '../../utils/escapeRegex'
import type { API } from '../api/loaders/[handler]'

type GenericPageProps = InferGetServerSidePropsType<typeof getServerSideProps>
export const api = createFetcher<API>('/api/loaders/[handler]')

export default function GenericPage(props: GenericPageProps) {
	const router = useRouter()
	const { query } = useRouter()

	const [isDetailVisible, setIsDetailVisible] = useState(query.detail === 'true' ? true : false)
	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
	const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false)

	const product = props.data.getProduct
	const translate = useTranslate()

	const topPositiveReview = props.data.topReviews.positive.listProductReview[0]
	const topNegativeReview = props.data.topReviews.negative.listProductReview[0]

	const breadCrumbsItems = [
		{
			id: product.brand?.id ?? '',
			title: product.brand?.name,
			link: `${InternalLink.Brand}${product.brand?.slug}`,
		},
		product.productLine?.id && {
			id: product.productLine?.id ?? '',
			title: product.productLine?.name,
			link: `${InternalLink.ProductLine}${product.productLine?.slug}`,
		},
	]

	const tabs = [
		{
			title: translate('productPage.tab.summary'),
			content: (
				<SummaryTab
					product={product}
					excludedFeatures={props.listFeatures}
					vetDietInfo={props.data.getEvaluationInfo?.veterinaryDiet?.blocks}
				/>
			),
			contentMobile: (
				<SummaryTabMobile
					product={product}
					excludedFeatures={props.listFeatures}
					vetDietInfo={props.data.getEvaluationInfo?.veterinaryDiet?.blocks}
				/>
			),
		},
		{
			title: translate('productPage.tab.ingredients'),
			content: <IngredientsTab product={product} openModal={() => setIsSuggestionModalOpen(true)} />,
		},
		{
			title: translate('productPage.tab.manufacturer'),
			content: props.data.getBrand ? (
				<ManufacturerTab brand={props.data.getBrand} flavorList={props.listFlavors} />
			) : null,
		},
	]

	const toggleDetail = () => {
		if (isDetailVisible) {
			setIsDetailVisible(false)
			router.replace({ pathname: router.pathname, query: { ...query, detail: false } }, undefined, {
				scroll: false,
			})
		} else {
			setIsDetailVisible(true)
			router.replace({ pathname: router.pathname, query: { ...query, detail: true } }, undefined, {
				scroll: false,
			})
		}
	}

	const isAllowedToFullUse = useIsAllowedToFullUse()

	const { dog } = useActiveDog()

	const relatedProducts = useQuery({
		queryKey: ['relatedProducts', product.score, product.id, dog, product.metabolizedEnergy],
		queryFn: async () => {
			if (product.score && product.id) {
				return api('relatedProducts', {
					score: product.score,
					productId: product.id,
					dogInfo: dog,
				})
			}
		},
		placeholderData: keepPreviousData,
	})

	const higherScoreProducts = relatedProducts.data?.higherScoreProducts ?? []
	const lowerScoreProducts = relatedProducts.data?.lowerScoreProducts ?? []

	const descriptionBrand = product.productLine
		? `${product.brand?.name} ${product.productLine.name}`
		: product.brand?.name

	const description = `${translate(
		'product.seo.description.first',
	)} ${descriptionBrand}. ${translate('product.seo.description.second')} ${props.amounts.paginateBrand.pageInfo.totalCount} ${translate('product.seo.description.third')} ${props.amounts.paginateProduct.pageInfo.totalCount} ${translate('product.seo.description.fourth')}`

	return (
		<Layout
			hasBreadcrumbs
			breadcrumbsItems={breadCrumbsItems}
			footer={props.data.footer.getFooter}
			socials={props.data.socials.listSocial}
			seo={setSeoProduct(props.data.getProduct, description)}
			url={`${InternalLink.Product}${props.data.getProduct.slug}`}
		>
			{isLoginModalOpen && <LoginModal closeModal={() => setIsLoginModalOpen(false)} isModalOpen={isLoginModalOpen} />}
			<SuggestionModal isModalOpen={isSuggestionModalOpen} handleClose={() => setIsSuggestionModalOpen(false)} />
			<div className={style.content} id="link">
				<Container size="wide">
					<ProductIntroSummary
						product={product}
						key={product.id}
						openModal={() => setIsLoginModalOpen(true)}
						consumptionMessages={props.data.listConsumptionMessage ?? []}
					/>
				</Container>
				<div className={style.summary} id="detailedComposition">
					<Container size="wide">
						<div className={style.rating}>
							<h2 className={style.ratingTitle}>
								<Translation tr="productPage.ourReview.title" />
							</h2>
							<Rating rateAmount={product.score} isPlacedOnProductDetail />
						</div>
						{product.evaluation && (
							<div className={style.evaluationSummary}>
								<p className={style.paragraph}>
									<Translation tr="productPage.evaluation.suitable.first" />{' '}
									<span className={style.boldText}>{product.evaluation.nazev}</span>{' '}
									<Translation tr="productPage.evaluation.suitable.second" />{' '}
									<span className={style.boldText}>{product.evaluation.vetDieta}</span>{' '}
									<Translation tr="productPage.evaluation.suitable.third" />{' '}
									<span className={style.boldText}>
										{product.evaluation?.vek} {product.evaluation.velikostPlemen} {product.evaluation?.aktivita}
									</span>
									.
								</p>
								<p className={style.paragraph}>
									{product.evaluation.zdrojeZivocisnychBilkovinDle1Vyskytu?.toLowerCase() !==
										'chybí info od výrobce' && (
										<>
											<span className={style.boldText}>
												<Translation tr="productPage.evaluation.composition.first" />
											</span>{' '}
											<Translation tr="productPage.evaluation.composition.second" />{' '}
											<span className={clsx(style.boldText, style.lowerCaseText)}>
												{product.evaluation?.zdrojeZivocisnychBilkovinDle1Vyskytu}
											</span>
											.
										</>
									)}{' '}
									<Translation tr="productPage.evaluation.composition.third" />
								</p>
							</div>
						)}
						<div className={style.detailButtonWrapper}>
							<Button
								type={{ variant: 'naked' }}
								label={
									isDetailVisible ? translate('productPage.button.showLess') : translate('productPage.button.showMore')
								}
								iconAfter={
									<span className={clsx(style.icon, isDetailVisible && style.iconRotate)}>
										<SvgChevronDown />
									</span>
								}
								icon={{ fillPrimary: true }}
								onClick={toggleDetail}
							/>
						</div>
						<div className={style.detailWrapper}>
							<Collapsible open={isDetailVisible} hasLowerIndex slowerAnimation>
								<Container size="wide" disableGutters>
									<div className={style.detailContent}>
										{product.evaluation && (
											<div className={style.detailDescription}>
												<EvaluationTemplate
													evaluation={product.evaluation}
													evaluationInfo={props.data.getEvaluationInfo}
												/>
											</div>
										)}
										{product.detailedComposition !== '0' && (
											<SummaryBanner
												energy={
													product.metabolizedEnergy && product.metabolizedEnergy > 0
														? `${Math.round(product.metabolizedEnergy)} ${translate(
																'productPage.metabolizedEnergy.unit',
															)}`
														: translate('productPage.metabolizedEnergy.empty')
												}
												summaryTitles={props.data.listProductEvaluationSummary}
												activity={recommendedActivity(product.metabolizedEnergy)}
												features={props.data.listProductFeature}
												eliminationDiet={product.isSuitableForEliminationDiet ?? false}
												hasDetailedComposition
											/>
										)}
									</div>
									<div className={style.lessButton}>
										<Button
											type={{ variant: 'naked' }}
											label={translate('productPage.button.showLess')}
											iconAfter={
												<span className={clsx(style.icon, style.iconRotate)}>
													<SvgChevronDown />
												</span>
											}
											icon={{ fillPrimary: true }}
											onClick={() => {
												toggleDetail()
												router.push({ hash: 'detailedComposition' }, undefined, {
													scroll: false,
													shallow: true,
												})
											}}
										/>
									</div>
								</Container>
							</Collapsible>
						</div>
					</Container>
					<Container size="wide" variant="mobileStretched">
						<div className={style.tabsWrapper}>
							<Tabs tabItems={tabs} isProductLocated />
						</div>
					</Container>
				</div>

				<Container size="wide">
					<ListProductReview
						product={product}
						scoreList={props.data.listScore}
						openLoginModal={() => setIsLoginModalOpen(true)}
						topPositiveReview={topPositiveReview}
						topNegativeReview={topNegativeReview}
					/>
					{product.score !== null && (
						<div className={style.carouselWrapper}>
							<ProductCarousel
								title={
									isAllowedToFullUse
										? translate('productPage.carousel.title')
										: translate('productPage.carousel.title.notSigned')
								}
								buttonLink={dog ? InternalLink.MyResults : ''}
								loading={relatedProducts.isLoading}
								products={[...higherScoreProducts, ...lowerScoreProducts]}
							/>
						</div>
					)}
				</Container>
			</div>
		</Layout>
	)
}

// export const runtime = 'experimental-edge'
export const getServerSideProps = serverPropsHandler(async (context: GetServerSidePropsContext) => {
	context.res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')

	const content = websiteContent

	const productSlug = String(context.query.product_slug)

	const data = await content('query')({
		...LayoutFragment(),
		...ProductFeaturesFragment(productSlug),
		getProduct: [
			{
				by: {
					slug: productSlug,
				},
			},
			ProductDetailFragment(),
		],
		getBrand: [
			{
				by: {
					products: { slug: productSlug },
				},
			},

			BrandTabFragment(),
		],
		listConsumptionMessage: [{ orderBy: [{ numberOfDays: OrderDirection.asc }] }, ConsumptionMessageFragment()],
		listScore: [{}, ScoreFragment()],
		getEvaluationInfo: [
			{
				by: {
					unique: One.One,
				},
			},
			EvaluationInfoFragment(),
		],
		listProductEvaluationSummary: [{ orderBy: [{ remoteId: OrderDirection.asc }] }, ProductEvaluationSummaryFragment()],
		listItem: [
			{},
			{
				name: true,
				slug: true,
				aliases: [{}, { name: true }],
			},
		],
	})

	const getProduct = data.getProduct

	if (!getProduct) {
		throw new RespondWithNotFound('Produkt nenalezen')
	}

	const aliases: Record<string, string> = {}

	data.listItem.forEach((item) => {
		if (item.name && item.slug) {
			aliases[item.name.toLowerCase()] = item.slug
		}
		item.aliases.forEach((alias) => {
			if (alias.name && item.slug) {
				aliases[alias.name.toLowerCase()] = item.slug
			}
		})
	})

	const re = `\\b((${Object.keys(aliases)
		.sort((b, a) => a.length - b.length)
		.map(escapeRegExp)
		.join('|')})[^\\s,\\.\\?\\!]*)`
	const aliasRegex = new RegExp(re, 'gi')

	function findAliases(text: string | undefined) {
		const lala = text?.replace(aliasRegex, (match, a, b) => {
			b = (b ?? '').toLowerCase()
			const found = aliases[b]
			const blacklist = ['lené']
			if (blacklist.includes(match)) {
				return match
			} else if (found) {
				return `[${match}](/s/${encodeURIComponent(found)})`
			}

			console.log('lalalalaal', lala)
			return match
		})
		return lala
	}

	getProduct.detailedComposition = findAliases(getProduct.detailedComposition)
	getProduct.guaranteedAnalysis = findAliases(getProduct.guaranteedAnalysis)
	getProduct.firstFiveItems = findAliases(getProduct.firstFiveItems)

	const topReviews = await content('query')({
		__alias: {
			positive: {
				listProductReview: [
					{
						limit: 1,
						orderBy: [{ stats: { positive: OrderDirection.desc } }],
						filter: { score: { starsAmount: { gte: 3 } }, product: { slug: { eq: productSlug } } },
					},
					ProductReviewFragment(),
				],
			},
			negative: {
				listProductReview: [
					{
						limit: 1,
						orderBy: [{ stats: { positive: OrderDirection.desc } }],
						filter: { score: { starsAmount: { lt: 3 } }, product: { slug: { eq: productSlug } } },
					},
					ProductReviewFragment(),
				],
			},
		},
	})

	const amounts = await content('query')({
		paginateBrand: [{}, { pageInfo: { totalCount: true } }],
		paginateProduct: [{}, { pageInfo: { totalCount: true } }],
	})

	const subCategories = await content('query')({
		__alias: {
			features: {
				listItemSubCategory: [
					{
						orderBy: [{ remoteId: OrderDirection.asc }],
						filter: {
							not: {
								products: { slug: { in: [productSlug] } },
							},
						},
					},
					ItemSubCategorySummaryFragment(),
				],
			},
			flavors: {
				listItemSubCategory: [
					{
						orderBy: [{ flavorCategory: { name: OrderDirection.asc } }],
						filter: {
							isFlavorCategory: { eq: true },
							products: {
								id: {
									or: getProduct?.brand?.products.map((product) => ({ in: [product.id] })),
								},
							},
						},
					},
					ItemSubCategoryPreviewFragment(),
				],
			},
		},
	})

	return {
		props: {
			data: {
				...data,
				getProduct,
				topReviews,
			},
			amounts,
			listFeatures: subCategories.features.listItemSubCategory,
			listFlavors: subCategories.flavors.listItemSubCategory,
		},
	}
})
